<template lang="pug">
    section.abonement.document-templates-panel
        //- Order notifications live under Communication (they depend on the WhatsApp channel),
        //- but people look for order-event messaging here alongside the order documents. This
        //- quiet signpost bridges the two without duplicating the config.
        router-link.order-notifications-signpost(v-if="orderNotificationsAvailable" :to="{ name: 'settings-communication' }")
            i.el-icon-fa-whatsapp.fab
            span.order-notifications-signpost__text
                span.order-notifications-signpost__title {{ $t('Order notifications via WhatsApp') }}
                span.order-notifications-signpost__hint {{ $t('Send order confirmations, shipping updates and more as WhatsApp messages. Set them up in the communication settings.') }}
            i.el-icon-fa-arrow-right.fas.order-notifications-signpost__go

        //- Keep existing blocks section unchanged
        el-row(v-if="blocks && blocks.length")
            el-collapse(accordion)
                transition-group(name="slide-decent-vertical")
                    el-collapse-item(
                        v-for="block in blocks" :key="block.key"
                        v-if="checkConditions(block)"
                        :name="block.key"
                    )
                        template(slot="title")
                            FontAwesomeIcon.icon(v-if="block.icon" :icon="block.icon")
                            span {{ block.name }}
                        transition-group(name="slide-decent-vertical")
                            .row(
                                v-for="(group, i) in block.groups" :key="block.key + '_' + i"
                                v-if="checkConditions(group)"
                            )
                                transition-group.flex(name="slide-decent-vertical")
                                    SettingField.setting-field(
                                        v-for="field in group.fields"
                                        v-if="checkConditions(field)"
                                        :key="field.key"
                                        :field="field"
                                        :setting-values="settingValues"
                                    )

        //- Split layout for document templates

        .document-templates-layout(v-if="hasTemplates")
            //- Sidebar with tree navigation
            .sidebar
                .sidebar-header
                    span.title {{ $t('Document Templates') }}

                el-tree.template-tree(
                    ref="templateTree"
                    :data="templateTree"
                    :props="{ children: 'children', label: 'label' }"
                    :default-expanded-keys="defaultExpandedKeys"
                    :expand-on-click-node="false"
                    :highlight-current="true"
                    node-key="key"
                    @node-click="handleTreeNodeClick"
                )
                    template(slot-scope="{ node, data }")
                        span.custom-tree-node
                            FontAwesomeIcon.tree-icon(v-if="data.icon" :icon="data.icon")
                            span.label {{ data.label }}
                            el-tag.macro-tag(
                                v-if="data.labelExtra"
                                size="mini"
                                type="warning"
                            ) {{ data.labelExtra }}
                            el-tag.base-tag(
                                v-if="data.isBase"
                                size="mini"
                                type="info"
                            ) {{ $t('Base') }}

            //- Editor area

            .editor-area
                //- Empty state
                .empty-state(v-if="!activeTemplate")
                    .empty-state-content
                        FontAwesomeIcon.empty-icon(:icon="'fa-solid fa-file-code'")
                        p {{ $t('Select a template from the sidebar') }}

                //- Active template editor (LAZY LOADED with v-if)

                template(v-if="activeTemplate")
                    //- Breadcrumb navigation (shown when template has parent)
                    .breadcrumb-nav(v-if="breadcrumbPath.length > 1")
                        template(v-for="(crumb, index) in breadcrumbPath")
                            span.breadcrumb-separator(v-if="index > 0") /
                            a.breadcrumb-item(
                                :key="crumb.key"
                                :class="{ active: crumb.key === activeTemplateKey }"
                                @click="navigateToParent(crumb.key)"
                            )
                                FontAwesomeIcon.breadcrumb-icon(v-if="crumb.icon" :icon="crumb.icon")
                                span {{ crumb.name }}
                        .header-actions
                            el-button(
                                v-if="templateVariableGroups.length"
                                size="mini"
                                icon="el-icon-magic-stick"
                                @click="showVars = !showVars"
                            ) {{ $t('Available Variables') }}
                            el-button(
                                size="mini"
                                icon="el-icon-view"
                                @click="showPreview = true"
                            ) {{ $t('Preview') }}

                    //- Template header (only shown for base templates without breadcrumb)

                    .template-header(v-else)
                        FontAwesomeIcon.header-icon(v-if="activeTemplate.icon" :icon="activeTemplate.icon")
                        h3.template-title {{ $t(activeTemplate.name) }}
                        .header-actions
                            el-button(
                                v-if="templateVariableGroups.length"
                                size="mini"
                                icon="el-icon-magic-stick"
                                @click="showVars = !showVars"
                            ) {{ $t('Available Variables') }}
                            el-button(
                                size="mini"
                                icon="el-icon-view"
                                @click="showPreview = true"
                            ) {{ $t('Preview') }}

                    //- Info alert if present

                    .alert.info(v-if="activeTemplate.info") {{ $t(activeTemplate.info) }}

                    //- Template fields (LAZY LOADED - only this template's editors are mounted)

                    .template-fields
                        .field-wrapper(v-for="field in activeTemplate.fields" :key="field.key" :class="{ 'is-macro': field.isMacro }")
                            //- Macro indicator
                            .field-macro-tag(v-if="field.isMacro")
                                el-tag(size="mini" type="warning") {{ $t('Macro') }}

                            //- Monaco editor field

                            document-template-field.grow.document-template-field(
                                :field="field"
                                :disabled="field.disabled"
                                :completions="flatCompletionItems"
                                :macros="activeTemplateMacros"
                                @input="value => updateDocumentTemplatePartial({ key: field.key, template: value })"
                                @click.native="field.disabled ? toggleDefault(false, field) : null"
                            )

                            //- Field inheritance info (shown when field inherits from parent)

                            .field-inherit-info(v-if="field.inheritsFrom && field.disabled")
                                i.el-icon-info
                                span {{ $t('Inherits from') }}
                                a(@click="navigateToParent(field.inheritsFrom.templateKey)") {{ $t(field.inheritsFrom.templateName) }} / {{ $t(field.inheritsFrom.fieldName) }}

                            //- Default toggle switch

                            el-form-item
                                el-switch(
                                    :value="field.disabled"
                                    :active-text="$t('Use default {type}', { type: field.name })"
                                    @change="value => toggleDefault(value, field)"
                                )

        //- Fallback when no templates
        template(v-else)
            .alert.warning {{ $t('No document templates available') }}

        //- Preview drawer
        document-template-preview-drawer(
            :visible="showPreview"
            :template="activeTemplate"
            :template-name="activeTemplate ? $t(activeTemplate.name) : ''"
            :cms-id="activeCms ? activeCms.id : null"
            :shop-id="activeCms && activeCms.shop ? activeCms.shop.id : null"
            :partials="form.documentTemplatePartials"
            @close="showPreview = false"
        )

        //- Variables drawer
        el-drawer.vars-drawer(
            :title="$t('Available Variables')"
            :visible.sync="showVars"
            direction="rtl"
            size="50%"
            @open="onVarsDialogOpen"
        )
            .vars-drawer-inner
                .vars-search-wrap
                    i.el-icon-search
                    input.vars-search(
                        v-model="varSearch"
                        :placeholder="$t('Search variables…')"
                        @keydown.esc="showVars = false"
                        ref="varSearchInput"
                    )
                .vars-dialog-body
                    template(v-for="group in filteredVariableGroups")
                        .var-group(:key="group.label")
                            .var-group-header
                                span.var-group-label {{ group.label }}
                                span.var-group-desc(v-if="group.description") {{ group.description }}
                            .var-chips
                                span.var-chip(
                                    v-for="v in group.vars"
                                    :key="v.value"
                                    :title="$t('Click to copy')"
                                    @click="copyVar(v.value)"
                                )
                                    span.var-chip-label {{ v.label }}
                                    span.var-chip-value(v-text="'{{ ' + v.value + ' }}'")
                    .vars-empty(v-if="!filteredVariableGroups.length")
                        | {{ $t('No variables found') }}
</template>

<i18n>
{
    "en": {
        "Use default {type}": "Use default {type}",
        "Letter Head": "Header",
        "Subject info": "Twig variables are allowed. The result should be a single line without line breaks or HTML.",
        "Preview": "Preview"
    },
    "de": {
        "Document sender": "Absender für Dokumente",
        "Leave a field empty to keep using the shop's data.": "Leer lassen, um die Angaben des Shops zu verwenden.",
        "Company name": "Firmenname",
        "Street": "Straße",
        "Zipcode": "PLZ",
        "City": "Ort",
        "Phone": "Telefon",
        "Bank": "Kreditinstitut",
        "VAT ID": "USt-ID",
        "Tax number": "Steuernummer",
        "Preview": "Vorschau",
        "Order notifications via WhatsApp": "Bestellbenachrichtigungen per WhatsApp",
        "Send order confirmations, shipping updates and more as WhatsApp messages. Set them up in the communication settings.": "Sende Bestellbestätigungen, Versand-Updates und mehr als WhatsApp-Nachrichten. Richte sie in den Kommunikationseinstellungen ein.",
        "Document Templates": "Dokumentvorlagen",
        "Select a template from the sidebar": "Wählen Sie eine Vorlage aus der Seitenleiste",
        "Inherits from": "Erbt von",
        "Go to parent": "Zum übergeordneten Element",
        "Base": "Basis",
        "No document templates available": "Keine Dokumentvorlagen verfügbar",
        "Use default {type}": "Standard {type} verwenden",

        "Letters": "PDF-Dokumente",
        "Thermal Printing Rolls": "Thermodruckrollen",
        "Emails": "E-Mails",

        "Subject": "Betreff",
        "Subject info": "Twig-Variablen sind erlaubt. Das Ergebnis sollte eine einzelne Zeile sein und keine Zeilenumbrüche oder HTML enthalten.",
        "Header": "Kopfzeile",
        "Letter Head": "Briefkopf",
        "CSS": "CSS",
        "Body": "Hauptteil",
        "Footer": "Fußzeile",
        "PDF Settings": "PDF-Einstellungen",
        "Customer Address": "Kundenadresse",
        "Articles Table": "Artikeltabelle",
        "Summary": "Zusammenfassung",
        "Logs Table": "Protokolltabelle",
        "Report Head": "Listenkopf",
        "QR Code Poster": "QR-Code-Aushang",
        "Bookings Table": "Anmeldetabelle",

        "Thermal Printing Roll": "Thermodruckrolle",
        "Cash Receipt": "Kassenbon",
        "Cash Receipt Macros": "Kassenbon Makros",
        "Cash Movement Receipt": "Kassenbewegungsbeleg",
        "Cancellation Receipt": "Stornobeleg",
        "Pending Reservations": "Ausstehende Reservierungen",

        "Order": "Bestellung",
        "Order Confirmation": "Bestellbestätigung",
        "Order Macros": "Bestellung Makros",
        "Delivery Note": "Lieferschein",
        "Delivery Note Macros": "Lieferschein Makros",
        "Packing List": "Packliste",
        "Packing List Macros": "Packliste Makros",
        "Stock Report": "Bestandsliste",
        "Stock Report Macros": "Bestandsliste Makros",
        "Cash Report": "Kassenabschluss",
        "Inventory Protocol": "Inventur-Protokoll",
        "Inventory Protocol Macros": "Inventur-Protokoll Makros",
        "Invoice": "Rechnung",
        "Credit Note": "Gutschrift",
        "Status Changed": "Statusänderung",
        "Returns": "Widerruf",
        "Return": "Widerruf",
        "Return Received": "Widerruf erhalten",
        "Return Status Changed": "Widerruf-Statusänderung",
        "Return Reminder": "Widerruf-Erinnerung",
        "Return Lookup Link": "Widerruf-Link",
        "Return Form": "Widerrufsformular",
        "Return Slip": "Rücksendebeleg",
        "Return Items Table": "Artikeltabelle",
        "Refund Summary": "Erstattungs-Übersicht",
        "Coupon": "Gutschein",
        "Event Ticket": "Veranstaltungsticket",
        "Event Bookings": "Anmeldeliste",
        "Reset Password": "Passwort zurücksetzen",
        "Verify Email": "Email verifizieren",
        "Customer Communication": "Kundenkommunikation",
        "Contact Form Notification": "Kontaktformular-Benachrichtigung",
        "Ticket Reply": "Ticket-Antwort",
        "Ticket System": "Ticketsystem",
        "Ticket Details": "Ticketdetails",
        "Ticket Created": "Ticket erstellt",
        "Ticket Assigned": "Ticket zugewiesen",
        "Requester Replied": "Antwort der anfragenden Person",
        "Ticket Changed": "Ticket geändert",
        "Unassigned Reminder": "Erinnerung: nicht zugewiesen",
        "Macro": "Makro",
        "Available Variables": "Verfügbare Variablen",
        "Click to copy": "Klicken zum Kopieren",
        "Search variables…": "Variablen suchen…",
        "No variables found": "Keine Variablen gefunden"
    }
}
</i18n>

<script>
import _ from 'lodash';

import SettingField from './Partials/SettingField';
import { useSettingsStore, useSetupStore } from '@/stores';
import DocumentTemplateField from './Partials/DocumentTemplateField';
import DocumentTemplatePreviewDrawer from './Partials/DocumentTemplatePreviewDrawer';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// Document templates are only relevant for the extensions a shop actually runs.
// Each template (or group/base) maps to the extension key that must be active
// for it to show — e.g. no order/return/PDF documents without the shop
// extension, no member emails without restricted-area, no till-roll documents
// without point-of-sale. Keys without an entry (the email base) are always
// shown when at least one of their children survives the filter.
const REQUIRED_EXTENSION = {
    // shop — order, return and PDF documents
    'pdf-letter': 'shop',
    'purchase-macros:email': 'shop',
    'purchase-macros:pdf': 'shop',
    'purchase-confirmation:email': 'shop',
    'purchase-confirmation:pdf': 'shop',
    'invoice:email': 'shop',
    'invoice:pdf': 'shop',
    'credit-note:email': 'shop',
    'credit-note:pdf': 'shop',
    'purchase-status-changed:email': 'shop',
    'return-macros': 'shop',
    'return-received': 'shop',
    'return-status-changed': 'shop',
    'return-reminder': 'shop',
    'return-lookup-link': 'shop',
    'return-form': 'shop',
    'return-slip': 'shop',
    'delivery-note:pdf': 'shop',
    'packing-list:pdf': 'shop',
    // POS documents — the cash report, inventory protocol and stock report are
    // all generated from PointOfSale mutations, so they require point-of-sale.
    'stock-report:pdf': 'shop:point-of-sale',
    'inventory-protocol:pdf': 'shop:point-of-sale',
    'cash-report:pdf': 'shop:point-of-sale',
    'coupon:pdf': 'shop',
    // event-manager — Anmeldeliste einer Veranstaltung
    'event-bookings:pdf': 'event-manager',
    // member account emails — members exist with the restricted-area extension
    // OR with shop (shop customers are members too), matching how the rest of
    // the app gates member features. An array means "any of these".
    'reset-password:email': ['restricted-area', 'shop'],
    'verify-email:email': ['restricted-area', 'shop'],
    // forms — contact-form notification
    'form-received:email': 'forms',
    // customer communication — the wrapper around Nachrichten replies
    'communication:email': 'communication',
    // tickets — the public reply that is emailed to the requester
    'ticket-reply:email': 'tickets',
    // tickets — the internal notifications that reach the team, one document
    // per event (same "one gate for the group, one for each child, no :email
    // suffix" shape the single-format Return macro group already uses).
    'ticket-notification-macros': 'tickets',
    'ticket-notification-created': 'tickets',
    'ticket-notification-assigned': 'tickets',
    'ticket-notification-requester-replied': 'tickets',
    'ticket-notification-changed': 'tickets',
    'ticket-notification-stale': 'tickets',
    // point-of-sale — thermal till-roll documents
    'pdf-till-roll': 'shop:point-of-sale',
    'cash-receipt:thermal': 'shop:point-of-sale',
    'coupon:thermal': 'shop:point-of-sale',
    'pending-reservations:thermal': 'shop:point-of-sale',
    'cash-movement-receipt:thermal': 'shop:point-of-sale',
    'cancellation-receipt:thermal': 'shop:point-of-sale',
    // The printed ticket is a till document, so it needs the POS as well as the event manager.
    // An array here means "any of these", so the event-manager half is enforced where the node is
    // pushed into the tree instead.
    'event-ticket:thermal': 'shop:point-of-sale',
};

export default {
    name: 'AbonementDocumentTemplates',
    components: {
        SettingField,
        FontAwesomeIcon,
        DocumentTemplateField,
        DocumentTemplatePreviewDrawer,
    },
    data() {
        return {
            // Currently selected template key
            activeTemplateKey: null,
            defaultExpandedKeys: [
                'email',
                'pdf-letter',
                'pdf-till-roll',
                'purchase-macros:email',
                'purchase-macros:pdf',
                'return-macros',
                'ticket-notification-macros',
            ],
            showPreview: false,
            showVars: false,
            varSearch: '',
        };
    },
    computed: {
        // Order notifications are a shop + communication feature; only signpost them when both
        // are on (no WhatsApp channel = nothing to configure, no shop = no order events).
        orderNotificationsAvailable() {
            const extensions = useSetupStore().enabledExtensions || [];
            return extensions.includes('communication') && extensions.includes('shop');
        },
        settings() {
            const settings = _.get(this.form, 'config.template.settings') || [];
            return settings.find((el) => el.key === 'document-templates');
        },
        blocks() {
            return _.get(this.settings, 'blocks') || [];
        },

        /**
         * Default values for all template partials (from API options)
         */
        defaults() {
            return this.options.documentTemplatePartial.items.reduce((acc, el) => {
                acc[el.key] = el.value;
                return acc;
            }, {});
        },

        /**
         * User overrides for template partials (from form state)
         */
        overrides() {
            return this.form.documentTemplatePartials.reduce((acc, el) => {
                acc[el.key] = el.template;
                return acc;
            }, {});
        },

        /**
         * Templates map - builds sidebar-friendly structure directly from raw data
         * Each template has: key, name, icon, fields, format, isBase, isMacro, parentKey
         */
        templatesMap() {
            const map = {};
            const d = this.defaults;
            const o = this.overrides;

            // Helper to build a field object
            // inheritFrom: optional object { key: 'email:header', templateName: 'Emails', fieldName: 'Header' }
            const field = (key, name, type, opts, inheritFrom) => {
                const override = _.get(o, key);
                const ownDefault = _.get(d, key, '').trim();

                // If no own default, try to inherit from parent template field
                let defaultVal = ownDefault;
                let inheritsFrom = null;

                if (!ownDefault && inheritFrom) {
                    // First check if parent has an override, then fall back to parent's default
                    const parentOverride = _.get(o, inheritFrom.key);
                    const parentDefault = _.get(d, inheritFrom.key, '').trim();
                    defaultVal = parentOverride !== undefined ? parentOverride : parentDefault;

                    // Track inheritance info for UI
                    if (defaultVal) {
                        inheritsFrom = {
                            key: inheritFrom.key,
                            templateKey: inheritFrom.templateKey,
                            templateName: inheritFrom.templateName,
                            fieldName: inheritFrom.fieldName,
                        };
                    }
                }

                return {
                    key,
                    name: this.$t(name),
                    type,
                    disabled: override === undefined,
                    default: defaultVal,
                    template: override !== undefined ? override : defaultVal,
                    inheritsFrom,
                    ...opts,
                };
            };

            // ========== BASE TEMPLATES ==========

            // Email base
            map['email'] = {
                key: 'email',
                name: 'Emails',
                icon: 'fa-solid fa-at',
                format: 'email',
                isBase: true,
                fields: [
                    field('email:style', 'CSS', 'code-editor:css'),
                    field('email:header', 'Header', 'code-editor:twig'),
                    field('email:footer', 'Footer', 'code-editor:twig'),
                ],
            };

            // PDF Letter base
            map['pdf-letter'] = {
                key: 'pdf-letter',
                name: 'Letters',
                icon: 'fa-solid fa-envelope-open-text',
                format: 'pdf',
                isBase: true,
                fields: [
                    field('pdf-letter:style', 'CSS', 'code-editor:css'),
                    field('pdf-letter:header', 'Letter Head', 'code-editor:twig'),
                    field('pdf-letter:footer', 'Footer', 'code-editor:twig'),
                    field('letter:pdf-options', 'PDF Settings', 'code-editor:json'),
                ],
            };

            // Thermal Roll base
            map['pdf-till-roll'] = {
                key: 'pdf-till-roll',
                name: 'Thermal Printing Rolls',
                icon: 'fa-solid fa-receipt',
                format: 'thermal',
                isBase: true,
                fields: [
                    field('pdf-till-roll:style', 'CSS', 'code-editor:css'),
                    field('pdf-till-roll:header', 'Header', 'code-editor:twig'),
                    field('pdf-till-roll:footer', 'Footer', 'code-editor:twig'),
                    field('till-roll:pdf-options', 'PDF Settings', 'code-editor:json'),
                ],
            };

            // ========== PURCHASE MACROS ==========

            map['purchase-macros:email'] = {
                key: 'purchase-macros:email',
                name: 'Order',
                nameExtra: 'Macros',
                icon: 'fa-solid fa-clone',
                format: 'email',
                isBase: false,
                isMacro: true,
                parentKey: 'email',
                fields: [
                    field('purchase-email:customer-address', 'Customer Address', 'code-editor:twig'),
                    field('purchase-email:articles', 'Articles Table', 'code-editor:twig'),
                    field('purchase-email:summary', 'Summary', 'code-editor:twig'),
                ],
            };

            map['purchase-macros:pdf'] = {
                key: 'purchase-macros:pdf',
                name: 'Order',
                nameExtra: 'Macros',
                icon: 'fa-solid fa-clone',
                format: 'pdf',
                isBase: false,
                isMacro: true,
                parentKey: 'pdf-letter',
                fields: [
                    field('purchase-pdf:articles', 'Articles Table', 'code-editor:twig'),
                    field('purchase-pdf:summary', 'Summary', 'code-editor:twig'),
                ],
            };

            // Inheritance helpers
            const emailInherit = (fieldName) => {
                return {
                    key: 'email:' + fieldName,
                    templateKey: 'email',
                    templateName: 'Emails',
                    fieldName: fieldName === 'style' ? 'CSS' : fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
                };
            };

            const pdfInherit = (fieldName) => {
                return {
                    key: 'pdf-letter:' + fieldName,
                    templateKey: 'pdf-letter',
                    templateName: 'Letters',
                    fieldName:
                        fieldName === 'header' ? 'Letter Head' : fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
                };
            };

            const thermalInherit = (fieldName) => {
                return {
                    key: 'pdf-till-roll:' + fieldName,
                    templateKey: 'pdf-till-roll',
                    templateName: 'Thermal Printing Rolls',
                    fieldName: fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
                };
            };

            // ========== PURCHASE DOCUMENTS (Email + PDF) ==========

            // Order Confirmation
            map['purchase-confirmation:email'] = {
                key: 'purchase-confirmation:email',
                name: 'Order Confirmation',
                icon: 'fa-solid fa-file-invoice',
                format: 'email',
                isBase: false,
                parentKey: 'purchase-macros:email',
                fields: [
                    field('purchase-confirmation-email:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('purchase-confirmation-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field(
                        'purchase-confirmation-email:header',
                        'Header',
                        'code-editor:twig',
                        null,
                        emailInherit('header')
                    ),
                    field('purchase-confirmation-email:body', 'Body', 'code-editor:twig'),
                    field(
                        'purchase-confirmation-email:footer',
                        'Footer',
                        'code-editor:twig',
                        null,
                        emailInherit('footer')
                    ),
                ],
            };
            map['purchase-confirmation:pdf'] = {
                key: 'purchase-confirmation:pdf',
                name: 'Order Confirmation',
                icon: 'fa-solid fa-file-invoice',
                format: 'pdf',
                isBase: false,
                parentKey: 'purchase-macros:pdf',
                fields: [
                    field('purchase-confirmation-pdf:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field(
                        'purchase-confirmation-pdf:header',
                        'Letter Head',
                        'code-editor:twig',
                        null,
                        pdfInherit('header')
                    ),
                    field('purchase-confirmation-pdf:body', 'Body', 'code-editor:twig'),
                    field('purchase-confirmation-pdf:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('purchase-confirmation:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Invoice
            map['invoice:email'] = {
                key: 'invoice:email',
                name: 'Invoice',
                icon: 'fa-solid fa-file-invoice-dollar',
                format: 'email',
                isBase: false,
                parentKey: 'purchase-macros:email',
                fields: [
                    field('invoice-email:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('invoice-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('invoice-email:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('invoice-email:body', 'Body', 'code-editor:twig'),
                    field('invoice-email:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };
            map['invoice:pdf'] = {
                key: 'invoice:pdf',
                name: 'Invoice',
                icon: 'fa-solid fa-file-invoice-dollar',
                format: 'pdf',
                isBase: false,
                parentKey: 'purchase-macros:pdf',
                fields: [
                    field('invoice-pdf:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('invoice-pdf:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('invoice-pdf:body', 'Body', 'code-editor:twig'),
                    field('invoice-pdf:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('invoice:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Credit Note
            map['credit-note:email'] = {
                key: 'credit-note:email',
                name: 'Credit Note',
                icon: 'fa-solid fa-file-invoice-dollar',
                format: 'email',
                isBase: false,
                parentKey: 'purchase-macros:email',
                fields: [
                    field('credit-note-email:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('credit-note-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('credit-note-email:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('credit-note-email:body', 'Body', 'code-editor:twig'),
                    field('credit-note-email:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };
            map['credit-note:pdf'] = {
                key: 'credit-note:pdf',
                name: 'Credit Note',
                icon: 'fa-solid fa-file-invoice-dollar',
                format: 'pdf',
                isBase: false,
                parentKey: 'purchase-macros:pdf',
                fields: [
                    field('credit-note-pdf:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('credit-note-pdf:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('credit-note-pdf:body', 'Body', 'code-editor:twig'),
                    field('credit-note-pdf:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('credit-note:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Order Status Changed (email only)
            map['purchase-status-changed:email'] = {
                key: 'purchase-status-changed:email',
                name: 'Status Changed',
                icon: 'fa-solid fa-file-lines',
                format: 'email',
                isBase: false,
                parentKey: 'purchase-macros:email',
                fields: [
                    field('purchase-status-changed-email:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('purchase-status-changed-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field(
                        'purchase-status-changed-email:header',
                        'Header',
                        'code-editor:twig',
                        null,
                        emailInherit('header')
                    ),
                    field('purchase-status-changed-email:body', 'Body', 'code-editor:twig'),
                    field(
                        'purchase-status-changed-email:footer',
                        'Footer',
                        'code-editor:twig',
                        null,
                        emailInherit('footer')
                    ),
                ],
            };

            // ========== RETURN EMAIL TEMPLATES ==========
            // All four are dispatched as part of the Widerruf flow
            // (§ 356a BGB). Sibling group to purchase-macros:email.

            map['return-macros'] = {
                key: 'return-macros',
                name: 'Return',
                nameExtra: 'Macros',
                icon: 'fa-solid fa-clone',
                format: 'email',
                isBase: false,
                isMacro: true,
                parentKey: 'email',
                fields: [
                    field('return:items', 'Return Items Table', 'code-editor:twig'),
                    field('return:summary', 'Refund Summary', 'code-editor:twig'),
                ],
            };

            map['return-received'] = {
                key: 'return-received',
                name: 'Return Received',
                icon: 'fa-solid fa-file-import',
                format: 'email',
                isBase: false,
                parentKey: 'return-macros',
                fields: [
                    field('return-received:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('return-received:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('return-received:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('return-received:body', 'Body', 'code-editor:twig'),
                    field('return-received:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            map['return-status-changed'] = {
                key: 'return-status-changed',
                name: 'Return Status Changed',
                icon: 'fa-solid fa-file-pen',
                format: 'email',
                isBase: false,
                parentKey: 'return-macros',
                fields: [
                    field('return-status-changed:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('return-status-changed:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('return-status-changed:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('return-status-changed:body', 'Body', 'code-editor:twig'),
                    field('return-status-changed:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            map['return-reminder'] = {
                key: 'return-reminder',
                name: 'Return Reminder',
                icon: 'fa-solid fa-file-circle-exclamation',
                format: 'email',
                isBase: false,
                parentKey: 'return-macros',
                fields: [
                    field('return-reminder:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('return-reminder:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('return-reminder:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('return-reminder:body', 'Body', 'code-editor:twig'),
                    field('return-reminder:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            map['return-lookup-link'] = {
                key: 'return-lookup-link',
                name: 'Return Lookup Link',
                icon: 'fa-solid fa-file-shield',
                format: 'email',
                isBase: false,
                parentKey: 'return-macros',
                fields: [
                    field('return-lookup-link:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('return-lookup-link:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('return-lookup-link:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('return-lookup-link:body', 'Body', 'code-editor:twig'),
                    field('return-lookup-link:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            // Return form PDF — fillable Muster-Widerrufsformular per Anlage 2 BGB
            map['return-form'] = {
                key: 'return-form',
                name: 'Return Form',
                icon: 'fa-solid fa-file-signature',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('return-form:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('return-form:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('return-form:body', 'Body', 'code-editor:twig'),
                    field('return-form:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                ],
            };

            // Return slip PDF — per-cancellation Rücksendebeleg with a Code 128 barcode,
            // attached to the approved ReturnStatusChanged email
            map['return-slip'] = {
                key: 'return-slip',
                name: 'Return Slip',
                icon: 'fa-solid fa-barcode',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('return-slip:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('return-slip:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('return-slip:body', 'Body', 'code-editor:twig'),
                    field('return-slip:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                ],
            };

            // ========== FLAT EMAIL TEMPLATES ==========

            // Reset Password
            map['reset-password:email'] = {
                key: 'reset-password:email',
                name: 'Reset Password',
                icon: 'fa-solid fa-file-lines',
                format: 'email',
                isBase: false,
                parentKey: 'email',
                fields: [
                    field('reset-password:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('reset-password:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('reset-password:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('reset-password:body', 'Body', 'code-editor:twig'),
                    field('reset-password:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            // Verify Email
            map['verify-email:email'] = {
                key: 'verify-email:email',
                name: 'Verify Email',
                icon: 'fa-solid fa-file-lines',
                format: 'email',
                isBase: false,
                parentKey: 'email',
                fields: [
                    field('verify-email:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('verify-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('verify-email:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('verify-email:body', 'Body', 'code-editor:twig'),
                    field('verify-email:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            // Customer Communication — the styling wrapper around replies sent from
            // the Nachrichten inbox. No subject/body: those are the agent's composed
            // message; only the surrounding chrome (style/header/footer) is templated.
            map['communication:email'] = {
                key: 'communication:email',
                name: 'Customer Communication',
                icon: 'fa-solid fa-comments',
                format: 'email',
                isBase: false,
                parentKey: 'email',
                fields: [
                    field('communication-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('communication-email:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('communication-email:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            // Contact Form Notification — sent when a website visitor submits a contact form
            map['form-received:email'] = {
                key: 'form-received:email',
                name: 'Contact Form Notification',
                icon: 'fa-solid fa-envelope',
                format: 'email',
                isBase: false,
                parentKey: 'email',
                fields: [
                    field('form-received:subject', 'Subject', 'code-editor:twig', {
                        minHeight: 16,
                        info: this.$t('Subject info'),
                    }),
                    field('form-received:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('form-received:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('form-received:body', 'Body', 'code-editor:twig'),
                    field('form-received:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            map['ticket-reply:email'] = {
                key: 'ticket-reply:email',
                name: 'Ticket Reply',
                icon: 'fa-solid fa-ticket-simple',
                format: 'email',
                isBase: false,
                parentKey: 'email',
                fields: [
                    field('ticket-reply:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                    field('ticket-reply:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                    field('ticket-reply:body', 'Body', 'code-editor:twig'),
                    field('ticket-reply:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                ],
            };

            // ========== TICKET NOTIFICATION MACROS ==========
            // One document per event (new ticket, assignment, requester
            // reply, the creator's opt-in "changed" mail, the 48h nag) — the
            // same shape Order gives Order Confirmation vs. Invoice vs.
            // Credit Note: each has its own full style/header/footer/body,
            // style/header/footer inheriting from the shared email:*
            // defaults, exactly like Ticket Reply's own single document does.
            const ticketNotificationFields = (key) => [
                field(`${key}:style`, 'CSS', 'code-editor:css', null, emailInherit('style')),
                field(`${key}:header`, 'Header', 'code-editor:twig', null, emailInherit('header')),
                field(`${key}:body`, 'Body', 'code-editor:twig'),
                field(`${key}:footer`, 'Footer', 'code-editor:twig', null, emailInherit('footer')),
            ];

            // Every event's body repeats the same ticket-details block
            // (subject/status/priority/requester) — one shared macro, edited
            // once here, called by all 5 bodies as ticket_details(ticket).
            map['ticket-notification-macros'] = {
                key: 'ticket-notification-macros',
                name: 'Ticket System',
                nameExtra: 'Macros',
                icon: 'fa-solid fa-clone',
                format: 'email',
                isBase: false,
                isMacro: true,
                parentKey: 'email',
                fields: [field('ticket-notification:ticket-details', 'Ticket Details', 'code-editor:twig')],
            };

            map['ticket-notification-created'] = {
                key: 'ticket-notification-created',
                name: 'Ticket Created',
                icon: 'fa-solid fa-circle-plus',
                format: 'email',
                isBase: false,
                parentKey: 'ticket-notification-macros',
                fields: ticketNotificationFields('ticket-notification-created'),
            };
            map['ticket-notification-assigned'] = {
                key: 'ticket-notification-assigned',
                name: 'Ticket Assigned',
                icon: 'fa-solid fa-user-check',
                format: 'email',
                isBase: false,
                parentKey: 'ticket-notification-macros',
                fields: ticketNotificationFields('ticket-notification-assigned'),
            };
            map['ticket-notification-requester-replied'] = {
                key: 'ticket-notification-requester-replied',
                name: 'Requester Replied',
                icon: 'fa-solid fa-reply',
                format: 'email',
                isBase: false,
                parentKey: 'ticket-notification-macros',
                fields: ticketNotificationFields('ticket-notification-requester-replied'),
            };
            map['ticket-notification-changed'] = {
                key: 'ticket-notification-changed',
                name: 'Ticket Changed',
                icon: 'fa-solid fa-pen',
                format: 'email',
                isBase: false,
                parentKey: 'ticket-notification-macros',
                fields: ticketNotificationFields('ticket-notification-changed'),
            };
            map['ticket-notification-stale'] = {
                key: 'ticket-notification-stale',
                name: 'Unassigned Reminder',
                icon: 'fa-solid fa-clock',
                format: 'email',
                isBase: false,
                parentKey: 'ticket-notification-macros',
                fields: ticketNotificationFields('ticket-notification-stale'),
            };

            // ========== FLAT PDF TEMPLATES ==========

            // Delivery Note
            map['delivery-note:pdf'] = {
                key: 'delivery-note:pdf',
                name: 'Delivery Note',
                icon: 'fa-solid fa-truck',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('delivery-note:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('delivery-note:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('delivery-note:body', 'Body', 'code-editor:twig'),
                    field('delivery-note:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('delivery-note:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                    field('delivery-note:articles', 'Articles Table', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Packing List
            map['packing-list:pdf'] = {
                key: 'packing-list:pdf',
                name: 'Packing List',
                icon: 'fa-solid fa-box',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('shipping-package-slip:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field(
                        'shipping-package-slip:header',
                        'Letter Head',
                        'code-editor:twig',
                        null,
                        pdfInherit('header')
                    ),
                    field('shipping-package-slip:body', 'Body', 'code-editor:twig'),
                    field('shipping-package-slip:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('shipping-package-slip:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                    field('shipping-package-slip:articles', 'Articles Table', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Stock Report
            map['stock-report:pdf'] = {
                key: 'stock-report:pdf',
                name: 'Stock Report',
                icon: 'fa-solid fa-warehouse',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('article-stock-report:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('article-stock-report:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('article-stock-report:body', 'Body', 'code-editor:twig'),
                    field('article-stock-report:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('article-stock-report:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                    field('article-stock-report:table', 'Articles Table', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Inventory Protocol
            map['inventory-protocol:pdf'] = {
                key: 'inventory-protocol:pdf',
                name: 'Inventory Protocol',
                icon: 'fa-solid fa-clipboard-list',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('inventory-protocol:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('inventory-protocol:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('inventory-protocol:body', 'Body', 'code-editor:twig'),
                    field('inventory-protocol:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('inventory-protocol:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                    field('inventory-protocol:logs', 'Logs Table', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Cash Report
            map['cash-report:pdf'] = {
                key: 'cash-report:pdf',
                name: 'Cash Report',
                icon: 'fa-solid fa-cash-register',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('cash-report:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('cash-report:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('cash-report:body', 'Body', 'code-editor:twig'),
                    field('cash-report:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('cash-report:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                    field('cash-report:logs', 'Logs Table', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Event Bookings (Anmeldeliste)
            // Kopf, Fußzeile und PDF-Einstellungen haben eigene Defaults (Report
            // statt Brief mit Adressfenster) — deshalb ohne Vererbung von
            // pdf-letter, anders als bei den Shop-Dokumenten.
            map['event-bookings:pdf'] = {
                key: 'event-bookings:pdf',
                name: 'Event Bookings',
                icon: 'fa-solid fa-clipboard-user',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('event-bookings:style', 'CSS', 'code-editor:css'),
                    field('event-bookings:header', 'Report Head', 'code-editor:twig'),
                    field('event-bookings:body', 'Body', 'code-editor:twig'),
                    field('event-bookings:qr-body', 'QR Code Poster', 'code-editor:twig'),
                    field('event-bookings:footer', 'Footer', 'code-editor:twig'),
                    field('event-bookings:pdf-options', 'PDF Settings', 'code-editor:json'),
                    field('event-bookings:table', 'Bookings Table', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Coupon (PDF Letter)
            map['coupon:pdf'] = {
                key: 'coupon:pdf',
                name: 'Coupon',
                icon: 'fa-solid fa-ticket',
                format: 'pdf',
                isBase: false,
                parentKey: 'pdf-letter',
                fields: [
                    field('coupon-letter:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                    field('coupon-letter:header', 'Letter Head', 'code-editor:twig', null, pdfInherit('header')),
                    field('coupon-letter:body', 'Body', 'code-editor:twig'),
                    field('coupon-letter:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                    field('coupon-letter:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'letter:pdf-options',
                        templateKey: 'pdf-letter',
                        templateName: 'Letters',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Event Ticket (Email) — the mail the ticket itself arrives in
            if (this.enabledExtensions.includes('event-manager')) {
                map['event-ticket:email'] = {
                    key: 'event-ticket:email',
                    name: 'Event Ticket',
                    icon: 'fa-solid fa-ticket-simple',
                    format: 'email',
                    isBase: false,
                    parentKey: 'email',
                    fields: [
                        field('event-ticket-email:subject', 'Subject', 'code-editor:twig', {
                            minHeight: 16,
                            info: this.$t('Subject info'),
                        }),
                        field('event-ticket-email:style', 'CSS', 'code-editor:css', null, emailInherit('style')),
                        field('event-ticket-email:header', 'Header', 'code-editor:twig', null, emailInherit('header')),
                        field('event-ticket-email:body', 'Body', 'code-editor:twig'),
                        field('event-ticket-email:footer', 'Footer', 'code-editor:twig', null, emailInherit('footer')),
                    ],
                };
            }

            // Event Ticket (PDF Letter) — attached to the order confirmation
            if (this.enabledExtensions.includes('event-manager')) {
                map['event-ticket:pdf'] = {
                    key: 'event-ticket:pdf',
                    name: 'Event Ticket',
                    icon: 'fa-solid fa-ticket-simple',
                    format: 'pdf',
                    isBase: false,
                    parentKey: 'pdf-letter',
                    fields: [
                        field('event-ticket-letter:style', 'CSS', 'code-editor:css', null, pdfInherit('style')),
                        field(
                            'event-ticket-letter:header',
                            'Letter Head',
                            'code-editor:twig',
                            null,
                            pdfInherit('header')
                        ),
                        field('event-ticket-letter:body', 'Body', 'code-editor:twig'),
                        field('event-ticket-letter:footer', 'Footer', 'code-editor:twig', null, pdfInherit('footer')),
                        field('event-ticket-letter:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                            key: 'letter:pdf-options',
                            templateKey: 'pdf-letter',
                            templateName: 'Letters',
                            fieldName: 'PDF Settings',
                        }),
                    ],
                };
            }

            // ========== THERMAL ROLL TEMPLATES ==========

            // Cash Receipt
            map['cash-receipt:thermal'] = {
                key: 'cash-receipt:thermal',
                name: 'Cash Receipt',
                icon: 'fa-solid fa-receipt',
                format: 'thermal',
                isBase: false,
                parentKey: 'pdf-till-roll',
                fields: [
                    field('cash-receipt:style', 'CSS', 'code-editor:css', null, thermalInherit('style')),
                    field('cash-receipt:header', 'Header', 'code-editor:twig', null, thermalInherit('header')),
                    field('cash-receipt:body', 'Body', 'code-editor:twig'),
                    field('cash-receipt:footer', 'Footer', 'code-editor:twig', null, thermalInherit('footer')),
                    field('cash-receipt:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'till-roll:pdf-options',
                        templateKey: 'pdf-till-roll',
                        templateName: 'Thermal Printing Rolls',
                        fieldName: 'PDF Settings',
                    }),
                    field('cash-receipt:articles', 'Articles Table', 'code-editor:twig', { isMacro: true }),
                    field('cash-receipt:summary', 'Summary', 'code-editor:twig', { isMacro: true }),
                ],
            };

            // Coupon (Thermal)
            map['coupon:thermal'] = {
                key: 'coupon:thermal',
                name: 'Coupon',
                icon: 'fa-solid fa-ticket',
                format: 'thermal',
                isBase: false,
                parentKey: 'pdf-till-roll',
                fields: [
                    field('coupon-till-roll:style', 'CSS', 'code-editor:css', null, thermalInherit('style')),
                    field('coupon-till-roll:header', 'Header', 'code-editor:twig', null, thermalInherit('header')),
                    field('coupon-till-roll:body', 'Body', 'code-editor:twig'),
                    field('coupon-till-roll:footer', 'Footer', 'code-editor:twig', null, thermalInherit('footer')),
                    field('coupon-till-roll:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'till-roll:pdf-options',
                        templateKey: 'pdf-till-roll',
                        templateName: 'Thermal Printing Rolls',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Event Ticket (Thermal) — printed at the counter
            if (this.enabledExtensions.includes('event-manager')) {
                map['event-ticket:thermal'] = {
                    key: 'event-ticket:thermal',
                    name: 'Event Ticket',
                    icon: 'fa-solid fa-ticket-simple',
                    format: 'thermal',
                    isBase: false,
                    parentKey: 'pdf-till-roll',
                    fields: [
                        field('event-ticket-till-roll:style', 'CSS', 'code-editor:css', null, thermalInherit('style')),
                        field(
                            'event-ticket-till-roll:header',
                            'Header',
                            'code-editor:twig',
                            null,
                            thermalInherit('header')
                        ),
                        field('event-ticket-till-roll:body', 'Body', 'code-editor:twig'),
                        field(
                            'event-ticket-till-roll:footer',
                            'Footer',
                            'code-editor:twig',
                            null,
                            thermalInherit('footer')
                        ),
                        field('event-ticket-till-roll:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                            key: 'till-roll:pdf-options',
                            templateKey: 'pdf-till-roll',
                            templateName: 'Thermal Printing Rolls',
                            fieldName: 'PDF Settings',
                        }),
                    ],
                };
            }

            // Pending Reservations
            map['pending-reservations:thermal'] = {
                key: 'pending-reservations:thermal',
                name: 'Pending Reservations',
                icon: 'fa-solid fa-clock',
                format: 'thermal',
                isBase: false,
                parentKey: 'pdf-till-roll',
                fields: [
                    field(
                        'pending-reservation-purchases:style',
                        'CSS',
                        'code-editor:css',
                        null,
                        thermalInherit('style')
                    ),
                    field(
                        'pending-reservation-purchases:header',
                        'Header',
                        'code-editor:twig',
                        null,
                        thermalInherit('header')
                    ),
                    field('pending-reservation-purchases:body', 'Body', 'code-editor:twig'),
                    field(
                        'pending-reservation-purchases:footer',
                        'Footer',
                        'code-editor:twig',
                        null,
                        thermalInherit('footer')
                    ),
                    field('pending-reservation-purchases:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'till-roll:pdf-options',
                        templateKey: 'pdf-till-roll',
                        templateName: 'Thermal Printing Rolls',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Cash Movement Receipt (Kassenbewegungsbeleg)
            map['cash-movement-receipt:thermal'] = {
                key: 'cash-movement-receipt:thermal',
                name: 'Cash Movement Receipt',
                icon: 'fa-solid fa-arrow-right-arrow-left',
                format: 'thermal',
                isBase: false,
                parentKey: 'pdf-till-roll',
                fields: [
                    field('cash-movement-receipt:style', 'CSS', 'code-editor:css', null, thermalInherit('style')),
                    field('cash-movement-receipt:header', 'Header', 'code-editor:twig', null, thermalInherit('header')),
                    field('cash-movement-receipt:body', 'Body', 'code-editor:twig'),
                    field('cash-movement-receipt:footer', 'Footer', 'code-editor:twig', null, thermalInherit('footer')),
                    field('cash-movement-receipt:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'till-roll:pdf-options',
                        templateKey: 'pdf-till-roll',
                        templateName: 'Thermal Printing Rolls',
                        fieldName: 'PDF Settings',
                    }),
                ],
            };

            // Cancellation Receipt (Stornobeleg)
            map['cancellation-receipt:thermal'] = {
                key: 'cancellation-receipt:thermal',
                name: 'Cancellation Receipt',
                icon: 'fa-solid fa-rotate-left',
                format: 'thermal',
                isBase: false,
                parentKey: 'pdf-till-roll',
                fields: [
                    field('cancellation-receipt:style', 'CSS', 'code-editor:css', null, thermalInherit('style')),
                    field('cancellation-receipt:header', 'Header', 'code-editor:twig', null, thermalInherit('header')),
                    field('cancellation-receipt:body', 'Body', 'code-editor:twig'),
                    field('cancellation-receipt:footer', 'Footer', 'code-editor:twig', null, thermalInherit('footer')),
                    field('cancellation-receipt:pdf-options', 'PDF Settings', 'code-editor:json', null, {
                        key: 'till-roll:pdf-options',
                        templateKey: 'pdf-till-roll',
                        templateName: 'Thermal Printing Rolls',
                        fieldName: 'PDF Settings',
                    }),
                    field('cancellation-receipt:articles', 'Articles Table', 'code-editor:twig', { isMacro: true }),
                    field('cancellation-receipt:summary', 'Summary', 'code-editor:twig', { isMacro: true }),
                ],
            };

            return map;
        },

        /**
         * Check if templates are available
         */
        hasTemplates() {
            return Object.keys(this.templatesMap).length > 0;
        },

        /**
         * Hierarchical tree structure for sidebar navigation
         */
        templateTree() {
            // Email tree
            const emailTree = {
                key: 'email',
                label: this.$t('Emails'),
                icon: 'fa-solid fa-at',
                isBase: true,
                children: [],
            };

            // Order Macros group for Email
            const emailOrderGroup = {
                key: 'purchase-macros:email',
                label: this.$t('Order'),
                labelExtra: this.$t('Macros'),
                icon: 'fa-solid fa-clone',
                isMacro: true,
                children: [
                    {
                        key: 'purchase-confirmation:email',
                        label: this.$t('Order Confirmation'),
                        icon: 'fa-solid fa-file-invoice',
                    },
                    { key: 'invoice:email', label: this.$t('Invoice'), icon: 'fa-solid fa-file-invoice-dollar' },
                    {
                        key: 'credit-note:email',
                        label: this.$t('Credit Note'),
                        icon: 'fa-solid fa-file-invoice-dollar',
                    },
                    {
                        key: 'purchase-status-changed:email',
                        label: this.$t('Status Changed'),
                        icon: 'fa-solid fa-file-lines',
                    },
                ],
            };
            emailTree.children.push(emailOrderGroup);

            // Return Macros group — sibling to purchase-macros:email. Single
            // format (email) → no :email suffix on keys. Parent uses the
            // 'clone' icon to match the existing 'Order Macros' convention;
            // children use file-* icons matching the order-email entries
            // (file-invoice / file-lines / file-pen / etc.).
            const returnMacrosGroup = {
                key: 'return-macros',
                label: this.$t('Return'),
                labelExtra: this.$t('Macros'),
                icon: 'fa-solid fa-clone',
                isMacro: true,
                children: [
                    { key: 'return-received', label: this.$t('Return Received'), icon: 'fa-solid fa-file-import' },
                    {
                        key: 'return-status-changed',
                        label: this.$t('Return Status Changed'),
                        icon: 'fa-solid fa-file-pen',
                    },
                    {
                        key: 'return-reminder',
                        label: this.$t('Return Reminder'),
                        icon: 'fa-solid fa-file-circle-exclamation',
                    },
                    {
                        key: 'return-lookup-link',
                        label: this.$t('Return Lookup Link'),
                        icon: 'fa-solid fa-file-shield',
                    },
                ],
            };
            emailTree.children.push(returnMacrosGroup);

            // Flat email templates
            emailTree.children.push({
                key: 'reset-password:email',
                label: this.$t('Reset Password'),
                icon: 'fa-solid fa-file-lines',
            });
            emailTree.children.push({
                key: 'verify-email:email',
                label: this.$t('Verify Email'),
                icon: 'fa-solid fa-file-lines',
            });
            emailTree.children.push({
                key: 'form-received:email',
                label: this.$t('Contact Form Notification'),
                icon: 'fa-solid fa-envelope',
            });
            emailTree.children.push({
                key: 'communication:email',
                label: this.$t('Customer Communication'),
                icon: 'fa-solid fa-comments',
            });
            emailTree.children.push({
                key: 'ticket-reply:email',
                label: this.$t('Ticket Reply'),
                icon: 'fa-solid fa-ticket-simple',
            });
            const ticketNotificationMacrosGroup = {
                key: 'ticket-notification-macros',
                label: this.$t('Ticket System'),
                labelExtra: this.$t('Macros'),
                icon: 'fa-solid fa-clone',
                isMacro: true,
                children: [
                    {
                        key: 'ticket-notification-created',
                        label: this.$t('Ticket Created'),
                        icon: 'fa-solid fa-circle-plus',
                    },
                    {
                        key: 'ticket-notification-assigned',
                        label: this.$t('Ticket Assigned'),
                        icon: 'fa-solid fa-user-check',
                    },
                    {
                        key: 'ticket-notification-requester-replied',
                        label: this.$t('Requester Replied'),
                        icon: 'fa-solid fa-reply',
                    },
                    {
                        key: 'ticket-notification-changed',
                        label: this.$t('Ticket Changed'),
                        icon: 'fa-solid fa-pen',
                    },
                    {
                        key: 'ticket-notification-stale',
                        label: this.$t('Unassigned Reminder'),
                        icon: 'fa-solid fa-clock',
                    },
                ],
            };
            emailTree.children.push(ticketNotificationMacrosGroup);
            if (this.enabledExtensions.includes('event-manager')) {
                emailTree.children.push({
                    key: 'event-ticket:email',
                    label: this.$t('Event Ticket'),
                    icon: 'fa-solid fa-ticket-simple',
                });
            }

            // PDF Letter tree
            const pdfTree = {
                key: 'pdf-letter',
                label: this.$t('Letters'),
                icon: 'fa-solid fa-envelope-open-text',
                isBase: true,
                children: [],
            };

            // Order Macros group for PDF
            const pdfOrderGroup = {
                key: 'purchase-macros:pdf',
                label: this.$t('Order'),
                labelExtra: this.$t('Macros'),
                icon: 'fa-solid fa-clone',
                isMacro: true,
                children: [
                    {
                        key: 'purchase-confirmation:pdf',
                        label: this.$t('Order Confirmation'),
                        icon: 'fa-solid fa-file-invoice',
                    },
                    { key: 'invoice:pdf', label: this.$t('Invoice'), icon: 'fa-solid fa-file-invoice-dollar' },
                    { key: 'credit-note:pdf', label: this.$t('Credit Note'), icon: 'fa-solid fa-file-invoice-dollar' },
                ],
            };
            pdfTree.children.push(pdfOrderGroup);

            // Flat PDF templates
            pdfTree.children.push({
                key: 'delivery-note:pdf',
                label: this.$t('Delivery Note'),
                icon: 'fa-solid fa-truck',
            });
            pdfTree.children.push({ key: 'packing-list:pdf', label: this.$t('Packing List'), icon: 'fa-solid fa-box' });
            pdfTree.children.push({
                key: 'return-form',
                label: this.$t('Return Form'),
                icon: 'fa-solid fa-file-signature',
            });
            pdfTree.children.push({ key: 'return-slip', label: this.$t('Return Slip'), icon: 'fa-solid fa-barcode' });
            pdfTree.children.push({
                key: 'stock-report:pdf',
                label: this.$t('Stock Report'),
                icon: 'fa-solid fa-warehouse',
            });
            pdfTree.children.push({
                key: 'inventory-protocol:pdf',
                label: this.$t('Inventory Protocol'),
                icon: 'fa-solid fa-clipboard-list',
            });
            pdfTree.children.push({
                key: 'cash-report:pdf',
                label: this.$t('Cash Report'),
                icon: 'fa-solid fa-cash-register',
            });
            pdfTree.children.push({ key: 'coupon:pdf', label: this.$t('Coupon'), icon: 'fa-solid fa-ticket' });
            pdfTree.children.push({
                key: 'event-bookings:pdf',
                label: this.$t('Event Bookings'),
                icon: 'fa-solid fa-clipboard-user',
            });
            if (this.enabledExtensions.includes('event-manager')) {
                pdfTree.children.push({
                    key: 'event-ticket:pdf',
                    label: this.$t('Event Ticket'),
                    icon: 'fa-solid fa-ticket-simple',
                });
            }

            // Thermal Roll tree
            const thermalTree = {
                key: 'pdf-till-roll',
                label: this.$t('Thermal Printing Rolls'),
                icon: 'fa-solid fa-receipt',
                isBase: true,
                children: [
                    { key: 'cash-receipt:thermal', label: this.$t('Cash Receipt'), icon: 'fa-solid fa-receipt' },
                    { key: 'coupon:thermal', label: this.$t('Coupon'), icon: 'fa-solid fa-ticket' },
                    {
                        key: 'pending-reservations:thermal',
                        label: this.$t('Pending Reservations'),
                        icon: 'fa-solid fa-clock',
                    },
                    {
                        key: 'cash-movement-receipt:thermal',
                        label: this.$t('Cash Movement Receipt'),
                        icon: 'fa-solid fa-arrow-right-arrow-left',
                    },
                    {
                        key: 'cancellation-receipt:thermal',
                        label: this.$t('Cancellation Receipt'),
                        icon: 'fa-solid fa-rotate-left',
                    },
                ],
            };

            // Needs the event manager on top of the POS requirement declared in the extension map.
            if (this.enabledExtensions.includes('event-manager')) {
                thermalTree.children.push({
                    key: 'event-ticket:thermal',
                    label: this.$t('Event Ticket'),
                    icon: 'fa-solid fa-ticket-simple',
                });
            }

            // Hide templates whose extension isn't active on this shop, then
            // prune any group/base left without surviving children.
            return this.filterTreeByExtension([emailTree, pdfTree, thermalTree]);
        },

        /**
         * Currently active template object
         */
        activeTemplate() {
            // A template that became unavailable (extension toggled off) must not
            // stay selected.
            if (this.activeTemplateKey && !this.isTemplateAvailable(this.activeTemplateKey)) {
                return null;
            }
            if (!this.activeTemplateKey) {
                return null;
            }
            return _.get(this.templatesMap, this.activeTemplateKey) || null;
        },

        /**
         * Breadcrumb path for current template
         */
        breadcrumbPath() {
            if (!this.activeTemplateKey || !this.activeTemplate) {
                return [];
            }

            const path = [];
            let currentKey = this.activeTemplateKey;
            const map = this.templatesMap;

            while (currentKey) {
                const template = map[currentKey];
                if (template) {
                    path.unshift({
                        key: currentKey,
                        name: this.$t(template.name) + (template.nameExtra ? ' (' + template.nameExtra + ')' : ''),
                        icon: template.icon,
                    });
                    currentKey = template.parentKey;
                } else {
                    break;
                }
            }

            return path;
        },

        templateVariableGroups() {
            const key = this.activeTemplateKey;
            if (!key) return [];

            const v = (value, label) => ({ value, label });

            const CMS = {
                label: 'cms',
                description: 'Shop-Einstellungen',
                vars: [
                    v('cms.name', 'Name'),
                    v('cms.sub_title', 'Untertitel'),
                    v('cms.logo', 'Logo-URL'),
                    v('cms.domain', 'Domain'),
                ],
            };
            // Logo rendering descriptor for the email header — url plus the
            // display width/height fitted to the 350×80 box. Lives in its own
            // `logo` scope (not on cms).
            const LOGO = {
                label: 'logo',
                description: 'Logo (E-Mail-Kopf)',
                vars: [
                    v('logo.url', 'Logo-URL'),
                    v('logo.width', 'Breite in px (auf 350×80 skaliert)'),
                    v('logo.height', 'Höhe in px (auf 350×80 skaliert)'),
                ],
            };
            const SENDER = {
                label: 'sender',
                description: 'Absender',
                vars: [v('sender.name', 'Name'), v('sender.one_liner', 'Kurztext'), v('sender.contact', 'Kontakt')],
            };
            const SENDER_SIMPLE = {
                label: 'sender',
                description: 'Absender',
                vars: [v('sender.name', 'Name')],
            };
            const COMPANY = {
                label: 'company',
                description: 'Unternehmensdaten',
                vars: [v('company', 'Unternehmen')],
            };
            const CUSTOMER = {
                label: 'customer',
                description: 'Kundendaten',
                vars: [
                    v('customer.nr', 'Kundennummer'),
                    v('customer.name', 'Vollständiger Name'),
                    v('customer.first_name', 'Vorname'),
                    v('customer.last_name', 'Nachname'),
                    v('customer.gender', 'Geschlecht'),
                    v('customer.title', 'Titel'),
                    v('customer.salutation', 'Anrede'),
                    v('customer.email', 'E-Mail'),
                    v('customer.phone', 'Telefon'),
                    v('customer.mobile', 'Mobil'),
                    v('customer.company', 'Firma'),
                    v('customer.company_addition', 'Firmenzusatz'),
                    v('customer.customer_group.name', 'Kundengruppe Name'),
                    v('customer.customer_group.key', 'Kundengruppe Schlüssel'),
                    v('customer.tags', 'Tags'),
                ],
            };
            const BILLING = {
                label: 'customer.billing_address',
                description: 'Rechnungsadresse',
                vars: [
                    v('customer.billing_address.name', 'Name'),
                    v('customer.billing_address.company', 'Firma'),
                    v('customer.billing_address.company_addition', 'Firmenzusatz'),
                    v('customer.billing_address.street', 'Straße'),
                    v('customer.billing_address.street_addition', 'Adresszusatz'),
                    v('customer.billing_address.zipcode', 'PLZ'),
                    v('customer.billing_address.city', 'Stadt'),
                    v('customer.billing_address.country', 'Land'),
                    v('customer.billing_address.phone', 'Telefon'),
                ],
            };
            const SHIPPING = {
                label: 'customer.shipping_address',
                description: 'Lieferadresse',
                vars: [
                    v('customer.shipping_address.name', 'Name'),
                    v('customer.shipping_address.company', 'Firma'),
                    v('customer.shipping_address.company_addition', 'Firmenzusatz'),
                    v('customer.shipping_address.street', 'Straße'),
                    v('customer.shipping_address.street_addition', 'Adresszusatz'),
                    v('customer.shipping_address.zipcode', 'PLZ'),
                    v('customer.shipping_address.city', 'Stadt'),
                    v('customer.shipping_address.country', 'Land'),
                    v('customer.shipping_address.phone', 'Telefon'),
                ],
            };
            const RECIPIENT = {
                label: 'recipient',
                description: 'Empfänger (Adressblock)',
                vars: [
                    v('recipient.block', 'Formatierter Adressblock'),
                    v('recipient.name', 'Vollständiger Name'),
                    v('recipient.first_name', 'Vorname'),
                    v('recipient.last_name', 'Nachname'),
                    v('recipient.gender', 'Geschlecht'),
                    v('recipient.title', 'Titel'),
                    v('recipient.salutation', 'Anrede'),
                    v('recipient.email', 'E-Mail'),
                    v('recipient.phone', 'Telefon'),
                    v('recipient.mobile', 'Mobil'),
                    v('recipient.company', 'Firma'),
                    v('recipient.company_addition', 'Firmenzusatz'),
                ],
            };
            const ORDER_ITEMS = {
                label: 'item',
                description: '{% for item in order.items %}',
                vars: [
                    v('item.nr', 'Artikelnummer'),
                    v('item.sku', 'SKU'),
                    v('item.name', 'Artikelname'),
                    v('item.description', 'Beschreibung'),
                    v('item.qty', 'Menge'),
                    v('item.price', 'Preis (roh)'),
                    v('item.sale_price', 'Sonderpreis (roh)'),
                    v('item.net_price', 'Nettopreis (roh)'),
                    v('item.sale_net_price', 'Sonder-Nettopreis (roh)'),
                    v('item.tax_value', 'Steuerwert'),
                    v('item.formatted_single_price', 'Einzelpreis formatiert'),
                    v('item.formatted_sale_price', 'Sonderpreis formatiert'),
                    v('item.formatted_total_price', 'Gesamtpreis formatiert'),
                ],
            };
            const ITEM_PROPERTIES = {
                label: 'property',
                description: '{% for property in item.selected_properties %}',
                vars: [v('property.key', 'Schlüssel'), v('property.label', 'Bezeichnung'), v('property.value', 'Wert')],
            };
            const ITEM_COUPONS = {
                label: 'coupon',
                description: '{% for coupon in item.coupons %}',
                vars: [
                    v('coupon.code', 'Code'),
                    v('coupon.name', 'Name'),
                    v('coupon.type', 'Typ (percentage/fixed)'),
                    v('coupon.percentage', 'Prozentwert'),
                    v('coupon.formatted_amount', 'Betrag formatiert'),
                ],
            };
            const ORDER_SUMMARY = {
                label: 'order.summary',
                description: 'Zusammenfassung',
                vars: [
                    v('order.summary.qty', 'Gesamtanzahl Artikel'),
                    v('order.summary.tax_total.formatted', 'MwSt. formatiert'),
                    v('order.summary.grand_total.formatted', 'Gesamtbetrag formatiert'),
                ],
            };
            const ORDER_SUMMARY_DETAILS = {
                label: 'detail',
                description: '{% for detail in order.summary.details %}',
                vars: [v('detail.label', 'Bezeichnung'), v('detail.formatted', 'Betrag formatiert')],
            };
            const ORDER = {
                label: 'order',
                description: 'Bestelldaten',
                vars: [
                    v('order.id', 'Bestell-ID'),
                    v('order.invoice_nr', 'Rechnungsnummer'),
                    v('order.created_at', 'Bestelldatum'),
                    v('order.payment_method', 'Zahlungsart'),
                    v('order.payment_method_service', 'Zahlungsservice'),
                    v('order.payment_method_info', 'Zahlungsinfo'),
                    v('order.payment_method_description', 'Zahlungsbeschreibung'),
                    v('order.payed_at', 'Bezahlt am'),
                    v('order.shipping_method', 'Versandart'),
                    v('order.shipping_method_service', 'Versandservice'),
                    v('order.shipping_method_info', 'Versandinfo'),
                    v('order.shipping_method_description', 'Versandbeschreibung'),
                    v('order.shipped_at', 'Versanddatum'),
                    v('order.shipping_fee', 'Versandkosten'),
                    v('order.should_be_delivered_at', 'Lieferdatum'),
                    v('order.tracking_nr', 'Trackingnummer'),
                    v('order.formatted_sub_total', 'Zwischensumme'),
                    v('order.formatted_shipping_fee', 'Versandkosten formatiert'),
                    v('order.formatted_tax_sums', 'Steuern'),
                    v('order.formatted_grand_total', 'Gesamtsumme'),
                    v('order.detailed_summary', 'Detailzusammenfassung'),
                    v('order.article_qty_sum', 'Artikelanzahl'),
                    v('order.commentary', 'Kommentar'),
                    v('order.status.key', 'Status Schlüssel'),
                    v('order.status.name', 'Status Name'),
                    v('order.items', 'Artikel (Array)'),
                ],
            };
            const RECIPIENT_MEMBER = {
                label: 'recipient',
                description: 'Empfänger',
                vars: [
                    v('recipient.name', 'Vollständiger Name'),
                    v('recipient.first_name', 'Vorname'),
                    v('recipient.last_name', 'Nachname'),
                    v('recipient.gender', 'Geschlecht'),
                    v('recipient.title', 'Titel'),
                    v('recipient.salutation', 'Anrede'),
                    v('recipient.email', 'E-Mail'),
                    v('recipient.phone', 'Telefon'),
                    v('recipient.mobile', 'Mobil'),
                    v('recipient.tags', 'Tags'),
                    v('recipient.address.name', 'Adresse Name'),
                    v('recipient.address.company', 'Adresse Firma'),
                    v('recipient.address.street', 'Straße'),
                    v('recipient.address.street_addition', 'Adresszusatz'),
                    v('recipient.address.zipcode', 'PLZ'),
                    v('recipient.address.city', 'Stadt'),
                    v('recipient.address.country', 'Land'),
                ],
            };
            const APP = {
                label: 'app',
                description: 'App-Konfiguration',
                vars: [v('app.name', 'App-Name'), v('app.homepage', 'Homepage'), v('app.url', 'URL')],
            };

            // Contact-form notification: the submitter and the submitted fields.
            const RECIPIENT_FORM = {
                label: 'recipient',
                description: 'Absender des Formulars',
                vars: [v('recipient.name', 'Name'), v('recipient.email', 'E-Mail')],
            };
            const FORM_FIELDS = {
                label: 'fields',
                description: 'Übermittelte Formularfelder (Liste)',
                vars: [
                    v('fields', 'Alle Felder (for field in fields)'),
                    v('field.type', 'Feld-Typ'),
                    v('field.title', 'Feld-Titel'),
                    v('field.value', 'Feld-Wert'),
                ],
            };

            // Variables the ticket reply body receives (see
            // App\Mails\Extensions\Tickets\TicketReply::individualize).
            const TICKET_FIELDS = {
                label: 'ticket',
                description: 'Ticket',
                vars: [
                    v('ticket.number', 'Ticketnummer (z. B. T-42)'),
                    v('ticket.subject', 'Betreff'),
                    v('reply', 'Antworttext der Bearbeitung'),
                ],
            };

            // Variables exposed to the return-* email body twigs and macros.
            // Built from $this->data['return'] in the four Mailables (see
            // ReturnReceived/ReturnStatusChanged/ReturnReturnReminder).
            const RETURN_OBJ = {
                label: 'return',
                description: 'Widerruf',
                vars: [
                    v('return.id', 'Vorgangsnummer'),
                    v('return.status', 'Status (pending | approved | rejected)'),
                    v('return.rejection_reason', 'Auslöser der Ablehnung (admin | expired)'),
                    v(
                        'return.rejection_cause',
                        'Festgestellter Ablehnungsgrund (Schlüssel, z. B. hygiene-seal-broken)'
                    ),
                    v('return.rejection_cause_label', 'Festgestellter Ablehnungsgrund (ausgeschriebener Text)'),
                    v('return.submitted_at', 'Eingangszeitpunkt'),
                    v('return.submitted_via', 'Eingangsweg (admin | member | guest | api)'),
                    v('return.comment', 'Allgemeiner Kommentar'),
                    v('return.admin_note', 'Notiz des Händlers (nur Status-Änderung)'),
                    v('return.credited_amount', 'Erstattungsbetrag (Zahl)'),
                    v('return.credited_amount_formatted', 'Erstattungsbetrag (formatiert)'),
                    v('return.refunded_at', 'Erstattungsdatum'),
                    v('return.audit_log_id', 'Audit-Log Referenz'),
                    v('return.days_remaining', 'Verbleibende Tage (nur Reminder)'),
                    v('return.items', 'Widerrufene Artikel (Array)'),
                ],
            };
            const RETURN_ITEM = {
                label: 'return.items[]',
                description: 'Eintrag in return.items',
                vars: [
                    v('item.id', 'ID des PurchasedItem'),
                    v('item.name', 'Artikelname'),
                    v('item.sku', 'SKU'),
                    v('item.pivot.qty', 'Widerrufene Menge'),
                    v('item.pivot.comment', 'Kommentar zu diesem Artikel'),
                    v('item.pivot.reason.id', 'Grund-ID'),
                    v('item.pivot.reason.key', 'Grund-Schlüssel'),
                    v('item.pivot.reason.name', 'Grund (lokalisiert)'),
                ],
            };
            const RETURN_LOOKUP = {
                label: 'return_lookup',
                description: 'Lookup-Link (Stage 1 Antwort)',
                vars: [v('return_lookup.signed_url', 'Signierter Link zur Bestellung (90 Tage gültig)')],
            };

            // Anmeldeliste einer Veranstaltung (EventBookings)
            const EVENT = {
                label: 'event',
                description: 'Veranstaltung',
                vars: [
                    v('event.id', 'Veranstaltungs-ID'),
                    v('event.name', 'Name'),
                    v('event.address', 'Ort (einzeilig)'),
                ],
            };
            // What the two ticket layouts get handed. `sessions` is the list a series pass admits
            // to - iterate it in the body to print every date.
            const EVENT_TICKET = {
                label: 'ticket',
                description: 'Ticket',
                vars: [
                    v('token', 'Ticket-Code (Klartext)'),
                    v('qr', 'QR-Code als data-URI'),
                    v('ticket.qr_token', 'Ticket-Code'),
                    v('ticket.created_at', 'ausgestellt am'),
                ],
            };
            const EVENT_SESSIONS = {
                label: 'sessions',
                description: 'Termine des Tickets',
                vars: [
                    v('sessions', 'Liste aller Termine (iterierbar)'),
                    v('sessions[0].start_at', 'Beginn'),
                    v('sessions[0].end_at', 'Ende'),
                    v('sessions|length', 'Anzahl Termine'),
                ],
            };
            const EVENT_STATS = {
                label: 'stats',
                description: 'Kennzahlen',
                vars: [
                    v('stats.total', 'Anmeldungen gesamt'),
                    v('stats.scanned', 'davon eingecheckt'),
                    v('stats.slots', 'Anzahl Termine'),
                    v('stats.first_slot', 'erster Termin'),
                    v('stats.last_slot', 'letzter Termin'),
                    v('printed_at', 'Druckzeitpunkt'),
                ],
            };
            const EVENT_SCAN = {
                label: 'scan',
                description: 'QR-Code der Scan-APP',
                vars: [
                    v('scan.url', 'Link zur Scan-APP (mit Vorauswahl)'),
                    v('scan.base_url', 'Link ohne Benutzer-Vorauswahl'),
                    v('scan.login', 'vorausgewählter App-Benutzer'),
                    v('scan.qr', 'QR-Code als Data-URI (img src)'),
                ],
            };
            const EVENT_COLUMNS = {
                label: 'column',
                description: '{% for column in columns %}',
                vars: [
                    v('column.key', 'Spalten-Schlüssel'),
                    v('column.label', 'Spaltentitel'),
                    v('column.center', 'zentriert (bool)'),
                ],
            };
            const EVENT_ROWS = {
                label: 'row',
                description: '{% for row in rows %}',
                vars: [
                    v('row.index', 'laufende Nummer'),
                    v('row.cells[column.key]', 'Zellwert der Spalte'),
                    v('row.scanned', 'eingecheckt (bool)'),
                    v('row.scanned_at', 'eingecheckt am'),
                ],
            };

            const purchaseGroups = [
                CMS,
                LOGO,
                SENDER,
                COMPANY,
                CUSTOMER,
                BILLING,
                SHIPPING,
                RECIPIENT,
                ORDER,
                ORDER_SUMMARY,
                ORDER_SUMMARY_DETAILS,
                ORDER_ITEMS,
                ITEM_PROPERTIES,
                ITEM_COUPONS,
            ];
            const memberGroups = [APP, CMS, LOGO, SENDER_SIMPLE, RECIPIENT_MEMBER];
            const returnGroups = [CMS, SENDER, COMPANY, CUSTOMER, BILLING, RECIPIENT, ORDER, RETURN_OBJ, RETURN_ITEM];
            const returnLookupGroups = [CMS, SENDER, COMPANY, CUSTOMER, BILLING, RECIPIENT, ORDER, RETURN_LOOKUP];

            const map = {
                email: [CMS, LOGO, SENDER],
                'pdf-letter': [CMS, SENDER],
                'pdf-till-roll': [CMS, SENDER],
                'purchase-macros:email': purchaseGroups,
                'purchase-macros:pdf': purchaseGroups,
                'purchase-confirmation:email': purchaseGroups,
                'purchase-confirmation:pdf': purchaseGroups,
                'invoice:email': purchaseGroups,
                'invoice:pdf': purchaseGroups,
                'credit-note:email': purchaseGroups,
                'credit-note:pdf': purchaseGroups,
                'purchase-status-changed:email': purchaseGroups,
                'return-macros': returnGroups,
                'return-received': returnGroups,
                'return-status-changed': returnGroups,
                'return-reminder': returnGroups,
                'return-lookup-link': returnLookupGroups,
                'return-form': returnGroups,
                'return-slip': returnGroups,
                'delivery-note:pdf': purchaseGroups,
                'packing-list:pdf': purchaseGroups,
                'stock-report:pdf': [CMS, SENDER],
                'inventory-protocol:pdf': [CMS, SENDER],
                'cash-report:pdf': [CMS, SENDER],
                'coupon:pdf': purchaseGroups,
                'event-bookings:pdf': [CMS, SENDER, EVENT, EVENT_STATS, EVENT_SCAN, EVENT_COLUMNS, EVENT_ROWS],
                'event-ticket:pdf': [CMS, LOGO, SENDER, RECIPIENT, EVENT, EVENT_TICKET, EVENT_SESSIONS],
                'event-ticket:thermal': [CMS, SENDER, EVENT, EVENT_TICKET, EVENT_SESSIONS],
                'coupon:thermal': purchaseGroups,
                'cash-receipt:thermal': purchaseGroups,
                'pending-reservations:thermal': purchaseGroups,
                'cash-movement-receipt:thermal': [CMS, SENDER],
                'cancellation-receipt:thermal': purchaseGroups,
                'reset-password:email': [
                    ...memberGroups,
                    {
                        label: 'Links',
                        description: 'Aktionslinks',
                        vars: [v('password_reset_url', 'Passwort-Reset-Link')],
                    },
                ],
                'verify-email:email': [
                    ...memberGroups,
                    {
                        label: 'Links',
                        description: 'Aktionslinks',
                        vars: [v('email_verify_url', 'E-Mail-Verifizierungslink')],
                    },
                ],
                'form-received:email': [CMS, LOGO, SENDER, RECIPIENT_FORM, FORM_FIELDS],
                'ticket-reply:email': [CMS, LOGO, SENDER, RECIPIENT_FORM, TICKET_FIELDS],
            };

            return map[key] || [];
        },

        filteredVariableGroups() {
            const search = (this.varSearch || '').toLowerCase().trim();
            if (!search) return this.templateVariableGroups;
            return this.templateVariableGroups
                .map((group) => ({
                    ...group,
                    vars: group.vars.filter(
                        (v) => v.value.toLowerCase().includes(search) || v.label.toLowerCase().includes(search)
                    ),
                }))
                .filter((group) => group.vars.length > 0);
        },

        flatCompletionItems() {
            return this.templateVariableGroups.flatMap((group) =>
                group.vars.map((v) => ({
                    value: v.value,
                    label: v.label,
                    group: group.label,
                }))
            );
        },

        /**
         * Twig macros callable from the active template's body.
         *
         * The macro list is derived from the active template key — both the
         * "always-on" macros that PurchaseConfirmation::individualize injects
         * for every email/PDF that goes through that render path, AND the
         * scope-conditional macros that only apply to the four return-* email
         * scopes (see the $isReturnScope branch in the same PHP file).
         *
         * Keeping this list HERE (in the parent) instead of in MonacoEditor
         * keeps the editor component domain-agnostic — it just receives an
         * array of `{ name, signature?, snippet?, desc? }` items.
         */
        activeTemplateMacros() {
            const key = this.activeTemplateKey;
            if (!key) return [];

            // Macros available to all email/PDF templates rendered through
            // the PurchaseConfirmation pipeline (and therefore the document-
            // template scope here in the admin).
            const baseMacros = [
                {
                    name: 'address_block',
                    signature: 'address_block(address, customer)',
                    snippet: 'address_block(${1:customer.billing_address}, ${2:customer})',
                    desc: this.$t('Render a customer address block'),
                },
                {
                    name: 'articles',
                    signature: 'articles(order)',
                    snippet: 'articles(${1:order})',
                    desc: this.$t('Render the order items table'),
                },
                {
                    name: 'summary',
                    signature: 'summary(order)',
                    snippet: 'summary(${1:order})',
                    desc: this.$t('Render the order summary block'),
                },
            ];

            // Return scopes get two extra Widerruf-specific callables. The
            // PHP side gates these by scope ∈ { return-received,
            // return-status-changed, return-reminder, return-lookup-link }
            // so we only offer them in the matching admin templates.
            const returnScopeKeys = [
                'return-received:email',
                'return-status-changed:email',
                'return-reminder:email',
                'return-lookup-link:email',
                'return-macros:email',
            ];
            if (returnScopeKeys.includes(key)) {
                baseMacros.push(
                    {
                        name: 'items',
                        signature: 'items(return)',
                        snippet: 'items(${1:return})',
                        desc: this.$t('Render the Widerruf items table'),
                    },
                    {
                        name: 'refund_summary',
                        signature: 'refund_summary(return)',
                        snippet: 'refund_summary(${1:return})',
                        desc: this.$t('Render the refund summary block'),
                    }
                );
            }

            return baseMacros;
        },

        form() {
            return useSettingsStore().form;
        },
        options() {
            return useSettingsStore().options;
        },
        settingValues() {
            return useSettingsStore().values;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        activeExtensions() {
            return useSetupStore().activeExtensions;
        },
        enabledFeatures() {
            return useSetupStore().enabledFeatures;
        },
    },
    methods: {
        /**
         * Handle template selection from sidebar tree
         */
        selectTemplate(templateKey) {
            this.activeTemplateKey = templateKey;
        },

        /**
         * Navigate to parent template from breadcrumb or inheritance link
         */
        navigateToParent(parentKey) {
            if (parentKey) {
                this.activeTemplateKey = parentKey;
                // Update tree selection to match
                if (this.$refs.templateTree) {
                    this.$refs.templateTree.setCurrentKey(parentKey);
                }
            }
        },

        /**
         * Handle el-tree node click
         */
        handleTreeNodeClick(data) {
            this.selectTemplate(data.key);
        },

        // A template is available when it requires no extension, or its required
        // extension is active on the current shop. Uses activeExtensions (the
        // CMS's actual active extensions) rather than enabledExtensions, which is
        // additionally narrowed by config.template.extensions and is empty for
        // shops that don't declare template-supported extensions.
        isTemplateAvailable(key) {
            const required = REQUIRED_EXTENSION[key];
            if (!required) return true;
            // An array means "any of these extensions" (OR).
            const list = Array.isArray(required) ? required : [required];
            return list.some((extension) => this.activeExtensions.includes(extension));
        },

        // Recursively drop unavailable templates and prune any group/base node
        // left with no surviving children.
        filterTreeByExtension(nodes) {
            return nodes
                .map((node) => {
                    if (!this.isTemplateAvailable(node.key)) return null;
                    if (node.children) {
                        const children = this.filterTreeByExtension(node.children);
                        if (!children.length) return null;
                        return { ...node, children };
                    }
                    return node;
                })
                .filter(Boolean);
        },

        checkConditions(setting) {
            let valid = true;

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            // eslint-disable-next-line no-unused-vars
            const enabledExtensions = this.enabledExtensions;
            // eslint-disable-next-line no-unused-vars
            const enabledFeatures = this.enabledFeatures;
            try {
                const conditions = setting.conditions;
                if (conditions && conditions.length) {
                    conditions.forEach((o) => {
                        if (valid === false) return;

                        const { type, condition } = o;
                        switch (type) {
                            case 'eval':
                                valid = eval(condition);
                                break;

                            default:
                                valid = false;
                        }
                    });
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error);
                valid = false;
            }

            return valid;
        },
        toggleDefault(useDefault, field) {
            if (!useDefault) {
                useSettingsStore().updateFormDocumentTemplatePartial({
                    key: field.key,
                    template: field.default,
                });
            } else {
                useSettingsStore().deleteFormDocumentTemplatePartial(field);
            }
        },
        updateDocumentTemplatePartial(data) {
            useSettingsStore().updateFormDocumentTemplatePartial(data);
        },
        copyVar(v) {
            const text = '{{ ' + v + ' }}';
            navigator.clipboard?.writeText(text).then(() => {
                this.$message({ message: text + ' kopiert', type: 'success', duration: 1500 });
            });
        },
        onVarsDialogOpen() {
            this.$nextTick(() => this.$refs.varSearchInput?.focus());
        },
    },
    // beforeRouteEnter(to, from, next) {
    //     let cms = _.get(store, 'state.setup.activeCms');
    //
    //     next(vm => {
    //         const settings = _.get(vm.form, 'config.template.settings')
    //             || _.get(vm.activeCms, 'config.template.settings')
    //             || []
    //
    //         if (!settings.find(el => el.key === 'document-templates')) {
    //             next({ name: 'settings-form' });
    //         } else {
    //             next();
    //         }
    //     })
    // }
};
</script>

<style lang="sass" scoped>
// Quiet signpost to the WhatsApp order-notification config (which lives
// under Communication). Deleted together with the editor markup in
// commit 3bf2462d and restored with it.
.order-notifications-signpost
    display: flex
    align-items: center
    gap: 12px
    // The panel itself is flush (see section.abonement.document-templates-panel),
    // so the signpost carries the inset the section used to provide.
    margin: 20px 20px 0
    padding: 14px 16px
    border: 1px solid #ebeef5
    border-radius: 10px
    background: #fafbfc
    text-decoration: none
    transition: border-color 0.15s, background 0.15s
    &:hover
        border-color: #409EFF
        background: #f5f9ff
        .order-notifications-signpost__go
            transform: translateX(2px)
            color: #409EFF
    > .fa-whatsapp
        flex-shrink: 0
        font-size: 22px
        color: #25d366
    .order-notifications-signpost__text
        display: flex
        flex-direction: column
        gap: 2px
        min-width: 0
    .order-notifications-signpost__title
        font-size: .86rem
        font-weight: 600
        color: #303133
    .order-notifications-signpost__hint
        font-size: .76rem
        line-height: 1.45
        color: #909399
    .order-notifications-signpost__go
        flex-shrink: 0
        margin-left: auto
        color: #c0c4cc
        transition: transform 0.15s, color 0.15s

// Existing styles (keep for blocks section)

.row
    display: flex
    margin-bottom: 12px
    & > span.flex
        width: 100%
        display: flex
        flex-wrap: wrap
        row-gap: 20px

.setting-field
    margin-right: 20px

.col-right
    img
        width: 90%

.el-main.main-slot .el-form .el-card .el-card__body section > .el-row > .el-col-lg-12:first-child:before
    display: none

.icon
    margin-right: 10px
    height: 18px
    width: 18px
    padding: 7px

.accordion-body
    font-size: 1rem

.flex-row
    gap: 2rem
    &.gap-sm
        gap: 0.5rem
    &.justify-center
        justify-content: center
    &.align-center
        align-items: center

hr.no-margin
    margin: 0

strong.name
    font-size: 1.2rem
    color: #333
    font-weight: 400

.document-template-field
    margin-top: 1rem
    margin-bottom: 0

// NEW: Split layout styles
// This tab is a PANEL, not a form: like a `.contains-table` card its content
// runs to the card edges and carries its own spacing, so the divider between
// the tree and the editor runs the full height instead of floating inside a
// 20px frame. The shell (.account :deep() .el-card__body > section) pads every
// section by 20px, which is the rule for the form tabs.
section.abonement.document-templates-panel
    padding: 0

.document-templates-layout
    display: flex
    gap: 0
    min-height: 600px
    // Separates the layout from the signpost / blocks above it, matching the
    // gap the signpost's own bottom margin used to give it. With neither
    // rendered it is the only child and the layout starts flush.
    margin-top: 20px

    &:first-child
        margin-top: 0

.sidebar
    flex: 0 0 300px
    min-width: 300px
    max-width: 340px
    border-right: 1px solid #ebeef5
    padding: 15px

    .sidebar-header
        border-bottom: 1px solid #ebeef5
        // No top padding: the editor column next to it starts at the panel's
        // own inset, so 12px here pushed the title 12px below its neighbour.
        // The bottom padding stays — it keeps the rule off the text.
        padding: 0 0 12px
        margin-bottom: 12px

        .title
            font-weight: 600
            font-size: 16px
            color: #303133

.template-tree
    ::v-deep .el-tree-node__content
        height: 36px
        padding-right: 8px

    ::v-deep .el-tree-node__expand-icon
        font-size: 14px

    ::v-deep .el-tree-node.is-current > .el-tree-node__content
        background-color: #ecf5ff

    ::v-deep .el-tree-node__content:hover
        background-color: #f5f7fa

.custom-tree-node
    display: flex
    align-items: center
    flex: 1
    padding: 4px 0
    font-size: 14px

    .tree-icon
        margin-right: 8px
        width: 16px
        height: 16px
        color: #606266

    .label
        flex: 1
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

    .base-tag
        margin-left: 8px
        flex-shrink: 0

.editor-area
    flex: 1
    min-width: 0
    padding: 15px

.empty-state
    display: flex
    align-items: center
    justify-content: center
    height: 300px
    background: #fafafa
    border-radius: 8px
    border: 2px dashed #dcdfe6

    .empty-state-content
        text-align: center
        color: #909399

        .empty-icon
            font-size: 48px
            margin-bottom: 16px
            opacity: 0.5

        p
            margin: 0
            font-size: 14px

.breadcrumb-nav
    display: flex
    align-items: center
    padding: 10px 14px
    background: #f5f7fa
    border-radius: 6px
    margin-bottom: 16px

    .breadcrumb-separator
        margin: 0 10px
        color: #909399
        font-weight: 300

    .breadcrumb-item
        display: flex
        align-items: center
        color: #606266
        cursor: pointer
        transition: color 0.2s
        font-size: 14px

        &:hover
            color: #409eff

        &.active
            color: #303133
            font-weight: 500
            cursor: default

            &:hover
                color: #303133

        .breadcrumb-icon
            margin-right: 6px
            width: 14px
            height: 14px

.header-actions
    margin-left: auto
    flex-shrink: 0
    display: flex
    gap: 8px

.template-header
    display: flex
    align-items: center
    margin-top: 8px
    margin-bottom: 23px

    .header-icon
        margin-right: 12px
        height: 1.2rem
        color: #606266

    .template-title
        margin: 0
        font-size: 20px
        font-weight: 600
        color: #303133

.template-fields
    .field-wrapper
        margin-bottom: 20px
        padding-bottom: 16px
        border-bottom: 1px solid #f0f0f0

        // Element UI's global `.el-form-item { margin-bottom: 22px }` adds
        // an extra gap below every wrapped code-editor field which stacks
        // with .field-wrapper's own bottom margin. Each editor is its own
        // .field-wrapper here, so we don't need the inner el-form-item
        // gap on top of that — drop it for any el-form-item rendered
        // inside the document-templates editor stack.
        &::v-deep .el-form-item
            margin-bottom: 0

        &:last-child
            border-bottom: none

        &.is-macro
            background: #fffbf0
            margin-left: -12px
            margin-right: -12px
            padding-left: 12px
            padding-right: 12px
            padding-top: 12px
            border-radius: 6px
            border: 1px dashed #e6a23c
            border-bottom: 1px dashed #e6a23c

    .field-macro-tag
        margin-bottom: 8px

        .el-tag
            font-size: 11px

    .field-inherit-info
        margin-top: 6px
        font-size: 12px
        line-height: 1.3
        color: #909399
        display: flex
        align-items: center
        gap: 6px

        i
            margin-top: 1px

        a
            color: #409eff
            cursor: pointer
            text-decoration: none

            &:hover
                text-decoration: underline

.vars-drawer
    ::v-deep .el-drawer__body
        padding: 0
        display: flex
        flex-direction: column
        overflow: hidden

.vars-drawer-inner
    display: flex
    flex-direction: column
    height: 100%

.vars-search-wrap
    display: flex
    align-items: center
    gap: 8px
    padding: 12px 16px
    border-bottom: 1px solid #ebeef5
    background: #fafafa

    i
        color: #909399
        font-size: 14px
        flex-shrink: 0

.vars-search
    flex: 1
    border: none
    outline: none
    background: transparent
    font-size: 13px
    color: #303133

    &::placeholder
        color: #c0c4cc

.vars-dialog-body
    flex: 1
    padding: 16px
    display: flex
    flex-direction: column
    gap: 16px
    overflow-y: auto

.var-group
    .var-group-header
        display: flex
        align-items: baseline
        gap: 8px
        margin-bottom: 7px

    .var-group-label
        font-size: 11px
        font-weight: 700
        color: #303133
        text-transform: uppercase
        letter-spacing: 0.06em

    .var-group-desc
        font-size: 11px
        color: #909399

    .var-chips
        display: flex
        flex-wrap: wrap
        gap: 5px

.vars-empty
    font-size: 13px
    color: #909399
    padding: 8px 0

.var-chip
    display: inline-flex
    align-items: center
    gap: 5px
    padding: 3px 8px
    background: #f4f7ff
    border: 1px solid #d0e0ff
    border-radius: 4px
    cursor: pointer
    white-space: nowrap
    transition: background 0.15s, border-color 0.15s

    &:hover
        background: #409eff
        border-color: #409eff

        .var-chip-label,
        .var-chip-value
            color: #fff

    .var-chip-label
        font-size: 11px
        color: #606266

    .var-chip-value
        font-size: 11px
        font-family: monospace
        color: #409eff
</style>
